import { Time } from 'lightweight-charts'
import { Point } from '../../../components/chartV2/drawingTools/drawing.interface'
import { getHeadAndShouldersTargetStatus } from './headAndShoulders'

export type ChartPatternKind =
    | 'HEAD_AND_SHOULDERS'
    | 'DOUBLE_TOP'
    | 'DOUBLE_BOTTOM'
    | 'CUP_AND_HANDLE'
    | 'RISING_WEDGE'
    | 'FALLING_WEDGE'
    | 'BEARISH_PENNANT'
    | 'BULLISH_PENNANT'
    | 'ASCENDING_TRIANGLE'
    | 'DESCENDING_TRIANGLE'
    | CandlestickPatternKind

export type CandlestickPatternKind =
    | 'BULLISH_ENGULFING'
    | 'HAMMER'
    | 'MORNING_STAR'
    | 'PIERCING_LINE'
    | 'BULLISH_HARAMI'
    | 'THREE_WHITE_SOLDIERS'
    | 'INVERTED_HAMMER'
    | 'DRAGONFLY_DOJI'
    | 'BULLISH_ABANDONED_BABY'
    | 'THREE_INSIDE_UP'
    | 'THREE_OUTSIDE_UP'
    | 'BULLISH_KICKER'
    | 'TWEEZER_BOTTOM'
    | 'RISING_THREE_METHODS'
    | 'CONCEALING_BABY_SWALLOW'
    | 'MAT_HOLD'
    | 'BULLISH_SEPARATING_LINES'
    | 'BULLISH_BELT_HOLD'
    | 'THREE_LINE_STRIKE_BULLISH'
    | 'LADDER_BOTTOM'
    | 'MEETING_LINES'
    | 'BEARISH_ENGULFING'
    | 'BEARISH_BELT_HOLD'
    | 'THREE_BLACK_CROWS'
    | 'THREE_LINE_STRIKE_BEARISH'
    | 'HANGING_MAN'
    | 'UPSIDE_GAP_TWO_CROWS'
    | 'EVENING_STAR'
    | 'SHOOTING_STAR'
    | 'BEARISH_HARAMI'
    | 'BEARISH_DOJI_STAR'
    | 'BEARISH_ABANDONED_BABY'
    | 'TWEEZER_TOP'
    | 'BEARISH_KICKER'
    | 'THREE_INSIDE_DOWN'
    | 'THREE_OUTSIDE_DOWN'
    | 'BEARISH_MAT_HOLD'
    | 'DARK_CLOUD_COVER'

export type ChartPatternLineStyle = 'solid' | 'dotted' | 'dashed'

export type ChartPatternLine = {
    from: string
    to: string
    style?: ChartPatternLineStyle
    color?: string
}

export type ChartPatternPriceLineSegment = {
    from: string
    to: string
    style?: ChartPatternLineStyle
    color?: string
    width?: number
}

export type ChartPatternLabel = {
    pointId: string
    text: string
    /** Wedge labels: rising = above, falling = below */
    position?: 'above' | 'below'
    color?: string
}

export type ChartPatternBias = 'bullish' | 'bearish'

export type ChartPatternDisplayMode = 'chart' | 'candlestick'

export type ChartPattern = {
    kind: ChartPatternKind
    points: Point[]
    polylineIds?: string[]
    priceLineSegments?: ChartPatternPriceLineSegment[]
    boundaryLines?: ChartPatternLine[]
    fillTriangles?: string[][]
    fillPolygonIds?: string[]
    labels?: ChartPatternLabel[]
    priceTarget?: number
    priceTargetUp?: number
    priceTargetDown?: number
    inProgress?: boolean
    targetAnchorPointId?: string
    /** IG-style directional bias for wedge, pennant, triangles, etc. */
    bias?: ChartPatternBias
    /** TradingView-style candlestick marker rendering */
    displayMode?: ChartPatternDisplayMode
    markerLetter?: string
    breakoutMarker?: {
        pointId: string
        text?: string
        color?: string
        position?: 'above' | 'below'
    }
    targetGuidePointId?: string
    targetGuidePrice?: number
    targetGuideColor?: string
    /** Pattern-specific fill override (e.g. ascending triangle green tint) */
    fillBackgroundColor?: string
    fillBackgroundOpacity?: number
    /** Up/down arrow drawn on a boundary-line endpoint (TradingView breakout arrow) */
    lineEndMarkers?: Array<{
        pointId: string
        direction: 'up' | 'down'
        color?: string
    }>
    candleHigh?: number
    candleLow?: number
    candleClose?: number
    /** TradingView-style vertical band highlight for each candle in the pattern */
    highlightCandleTimes?: Time[]
    description?: string
}

const CANDLESTICK_KIND_SET = new Set<string>([
    'BULLISH_ENGULFING', 'HAMMER', 'MORNING_STAR', 'PIERCING_LINE', 'BULLISH_HARAMI',
    'THREE_WHITE_SOLDIERS', 'INVERTED_HAMMER', 'DRAGONFLY_DOJI', 'BULLISH_ABANDONED_BABY',
    'THREE_INSIDE_UP', 'THREE_OUTSIDE_UP', 'BULLISH_KICKER', 'TWEEZER_BOTTOM',
    'RISING_THREE_METHODS', 'CONCEALING_BABY_SWALLOW', 'MAT_HOLD', 'BULLISH_SEPARATING_LINES',
    'BULLISH_BELT_HOLD', 'THREE_LINE_STRIKE_BULLISH', 'LADDER_BOTTOM', 'MEETING_LINES',
    'BEARISH_ENGULFING', 'BEARISH_BELT_HOLD', 'THREE_BLACK_CROWS', 'THREE_LINE_STRIKE_BEARISH',
    'HANGING_MAN', 'UPSIDE_GAP_TWO_CROWS', 'EVENING_STAR', 'SHOOTING_STAR', 'BEARISH_HARAMI',
    'BEARISH_DOJI_STAR', 'BEARISH_ABANDONED_BABY', 'TWEEZER_TOP', 'BEARISH_KICKER',
    'THREE_INSIDE_DOWN', 'THREE_OUTSIDE_DOWN', 'BEARISH_MAT_HOLD', 'DARK_CLOUD_COVER',
])

export function isCandlestickPatternKind(kind: ChartPatternKind): boolean {
    return CANDLESTICK_KIND_SET.has(kind)
}

export function isCandlestickChartPattern(pattern: ChartPattern): boolean {
    return pattern.displayMode === 'candlestick' || isCandlestickPatternKind(pattern.kind)
}

export type ChartPatternDetectOptions = {
    trendHeight?: number
    inProgress?: boolean
    priceTargetsFilter?: string
    patternsFilter?: string
    tolerancePct?: number
    /** Double top: Higher | Lower | Both */
    secondTop?: string
    /** Double bottom: Higher | Lower | Both */
    secondBottom?: string
    /** 0 = unlimited (internal); default 1 = latest pattern only */
    maxPatterns?: number
    /** Hammer / inverted hammer: SMA50 | SMA50, SMA200 | No detection */
    trendBasedOn?: string
    close?: number[]
    /** Ascending triangle — pivot swing length (default 3) */
    swingLength?: number
    minBarsForPattern?: number
    maxBarsForPattern?: number
    maxBarsForConfirmation?: number
    atrLength?: number
    minSwingProminenceAtr?: number
    minTriangleHeightAtr?: number
    maxTriangleHeightAtr?: number
    maxHorizontalToleranceAtr?: number
    /** Rising / falling wedge — TV Channel Width Ratio Start/End (percent) */
    channelWidthRatioPct?: number
    /** Bearish / Bullish Pennant — max rollback of point 3 (% of flagpole height) */
    maxRollbackPct?: number
}

export type ChartPatternTargetStatus = 'Awaiting' | 'In progress' | 'Reached' | 'Failed'

function priceOnTimeLine(t1: number, p1: number, t2: number, p2: number, t: number): number {
    if (t2 === t1) return p1
    return p1 + (p2 - p1) * (t - t1) / (t2 - t1)
}

/** TradingView Rising Wedge target statuses — see TV auto chart pattern docs */
export function getRisingWedgeTargetStatus(
    pattern: ChartPattern,
    candles: Array<{ time: Time; close: number; low: number; high: number }>,
): ChartPatternTargetStatus {
    if (pattern.inProgress) return 'Awaiting'
    if (pattern.priceTarget == null || !Number.isFinite(pattern.priceTarget)) return 'Awaiting'

    const h1 = pattern.points.find((p) => p.id === 'H1')
    const h2 = pattern.points.find((p) => p.id === 'H2')
    const u = pattern.points.find((p) => p.id === 'U')
    const anchor = pattern.points.find((p) => p.id === (pattern.targetAnchorPointId ?? 'BR'))
    if (!h1 || !h2 || !u || !anchor) return 'Awaiting'

    const anchorTime = anchor.x as number
    const afterBreakout = candles
        .filter((candle) => (candle.time as number) >= anchorTime)
        .slice(0, 200)
    if (!afterBreakout.length) return 'Awaiting'

    const upperExtreme = Math.max(h1.y, h2.y, u.y)
    const uTime = u.x as number

    if (afterBreakout.some((candle) => candle.low <= pattern.priceTarget!)) return 'Reached'

    for (const candle of afterBreakout) {
        const time = candle.time as number
        const upperAtBar = priceOnTimeLine(h1.x as number, h1.y, uTime, u.y, time)
        if (candle.close > upperAtBar || candle.high > upperExtreme) return 'Failed'
    }

    return 'Awaiting'
}

/** TradingView Falling Wedge target statuses */
export function getFallingWedgeTargetStatus(
    pattern: ChartPattern,
    candles: Array<{ time: Time; close: number; low: number; high: number }>,
): ChartPatternTargetStatus {
    if (pattern.inProgress) return 'Awaiting'
    if (pattern.priceTarget == null || !Number.isFinite(pattern.priceTarget)) return 'Awaiting'

    const l1 = pattern.points.find((p) => p.id === 'L1')
    const s = pattern.points.find((p) => p.id === 'S')
    const anchor = pattern.points.find((p) => p.id === (pattern.targetAnchorPointId ?? 'BR'))
    if (!l1 || !s || !anchor) return 'Awaiting'

    const anchorTime = anchor.x as number
    const afterBreakout = candles
        .filter((candle) => (candle.time as number) >= anchorTime)
        .slice(0, 200)
    if (!afterBreakout.length) return 'Awaiting'

    const lowerExtreme = Math.min(l1.y, s.y)
    const sTime = s.x as number

    if (afterBreakout.some((candle) => candle.high >= pattern.priceTarget!)) return 'Reached'

    for (const candle of afterBreakout) {
        const time = candle.time as number
        const lowerAtBar = priceOnTimeLine(l1.x as number, l1.y, sTime, s.y, time)
        if (candle.close < lowerAtBar || candle.low < lowerExtreme) return 'Failed'
    }

    return 'Awaiting'
}

/** TradingView Double Bottom target statuses */
export function getDoubleBottomTargetStatus(
    pattern: ChartPattern,
    candles: Array<{ time: Time; close: number; low: number; high: number }>,
): ChartPatternTargetStatus {
    if (pattern.inProgress) return 'Awaiting'
    if (pattern.priceTarget == null || !Number.isFinite(pattern.priceTarget)) return 'Awaiting'

    const bottom2 = pattern.points.find((p) => p.id === '4')
    const anchor = pattern.points.find((p) => p.id === (pattern.targetAnchorPointId ?? '5'))
    if (!bottom2 || !anchor) return 'Awaiting'

    const anchorTime = anchor.x as number
    const afterBreakout = candles
        .filter((candle) => (candle.time as number) >= anchorTime)
        .slice(0, 200)
    if (!afterBreakout.length) return 'Awaiting'

    if (afterBreakout.some((candle) => candle.high >= pattern.priceTarget!)) return 'Reached'
    if (afterBreakout.some((candle) => candle.close < bottom2.y || candle.low < bottom2.y)) return 'Failed'
    return 'Awaiting'
}

/** TradingView Double Top target statuses */
export function getDoubleTopTargetStatus(
    pattern: ChartPattern,
    candles: Array<{ time: Time; close: number; low: number; high: number }>,
): ChartPatternTargetStatus {
    if (pattern.inProgress) return 'Awaiting'
    if (pattern.priceTarget == null || !Number.isFinite(pattern.priceTarget)) return 'Awaiting'

    const top2 = pattern.points.find((p) => p.id === '4')
    const anchor = pattern.points.find((p) => p.id === (pattern.targetAnchorPointId ?? '5'))
    if (!top2 || !anchor) return 'Awaiting'

    const anchorTime = anchor.x as number
    const afterBreakout = candles
        .filter((candle) => (candle.time as number) >= anchorTime)
        .slice(0, 200)
    if (!afterBreakout.length) return 'Awaiting'

    if (afterBreakout.some((candle) => candle.low <= pattern.priceTarget!)) return 'Reached'
    if (afterBreakout.some((candle) => candle.close > top2.y || candle.high > top2.y)) return 'Failed'
    return 'Awaiting'
}

export function getChartPatternTargetStatus(
    pattern: ChartPattern,
    candles: Array<{ time: Time; close: number; low: number; high: number }>
): ChartPatternTargetStatus {
    if (pattern.kind === 'HEAD_AND_SHOULDERS') {
        return getHeadAndShouldersTargetStatus(
            pattern.points,
            pattern.priceTarget,
            candles,
            pattern.inProgress,
        )
    }
    if (pattern.kind === 'RISING_WEDGE') {
        return getRisingWedgeTargetStatus(pattern, candles)
    }
    if (pattern.kind === 'FALLING_WEDGE') {
        return getFallingWedgeTargetStatus(pattern, candles)
    }
    if (pattern.kind === 'DOUBLE_TOP') {
        return getDoubleTopTargetStatus(pattern, candles)
    }
    if (pattern.kind === 'DOUBLE_BOTTOM') {
        return getDoubleBottomTargetStatus(pattern, candles)
    }
    if (pattern.inProgress) return 'Awaiting'
    if (pattern.priceTarget == null || !Number.isFinite(pattern.priceTarget)) return 'Awaiting'

    const anchorId = pattern.targetAnchorPointId ?? pattern.points[pattern.points.length - 1]?.id
    const anchor = pattern.points.find((point) => point.id === anchorId)
    const neckline = pattern.boundaryLines?.[0]
    const necklineEnd = neckline
        ? pattern.points.find((point) => point.id === neckline.to)
        : anchor

    if (!anchor || !necklineEnd) return 'Awaiting'

    const anchorTime = anchor.x as number
    const afterPattern = candles
        .filter((candle) => (candle.time as number) >= anchorTime)
        .slice(0, 150)
    if (!afterPattern.length) return 'Awaiting'

    const isBearish = pattern.bias === 'bearish'
        || (pattern.bias == null && pattern.kind === 'DESCENDING_TRIANGLE')

    if (isBearish) {
        if (afterPattern.some((candle) => candle.low <= pattern.priceTarget!)) return 'Reached'
        const necklinePrice = necklineEnd.y
        if (!afterPattern.some((candle) => candle.close < necklinePrice)) return 'Awaiting'
        if (afterPattern.some((candle) => candle.close > necklinePrice)) return 'Failed'
        return 'In progress'
    }

    if (afterPattern.some((candle) => candle.high >= pattern.priceTarget!)) return 'Reached'
    const necklinePrice = necklineEnd.y
    if (!afterPattern.some((candle) => candle.close > necklinePrice)) return 'Awaiting'
    if (afterPattern.some((candle) => candle.close < necklinePrice)) return 'Failed'
    return 'In progress'
}
